import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, LucideIcon } from "lucide-react";
import TopicCard, { type TopicDisplaySession } from "@/components/topic/TopicCard";
import TopicCardSkeleton from "@/components/topic/TopicCardSkeleton";

export interface EmptyStateConfig {
    icon: LucideIcon;
    title: string;
    description: string;
    action?: {
        label: string;
        onClick: () => void;
        icon?: LucideIcon;
    };
}

export interface TopicListSectionProps {
    title: string;
    icon: LucideIcon;
    sessions: TopicDisplaySession[];
    loading?: boolean;
    isFetching?: boolean;
    emptyState?: EmptyStateConfig;
    showAllPath?: string;
    onShowAll?: () => void;
    onView: (session: TopicDisplaySession) => void;
    onPractice: (session: TopicDisplaySession) => void;
    showViewButton?: boolean;
    skeletonCount?: number;
    hideWhenEmpty?: boolean;
    variant?: "default" | "personal" | "public";
    dataTour?: string;
    /** Optional per-card badge (e.g. a study-group name) rendered inside each TopicCard. */
    renderCardBadge?: (session: TopicDisplaySession) => React.ReactNode;
}

const TopicListSection = memo(({
    title,
    icon: Icon,
    sessions,
    loading = false,
    isFetching = false,
    emptyState,
    showAllPath,
    onShowAll,
    onView,
    onPractice,
    showViewButton = true,
    skeletonCount = 6,
    hideWhenEmpty = false,
    variant = "default",
    dataTour,
    renderCardBadge,
}: TopicListSectionProps) => {
    const { t } = useTranslation();
    const hasData = sessions.length > 0;
    const showSkeletons = loading && !hasData;

    if (hideWhenEmpty && !loading && !hasData) {
        return null;
    }

    const handleShowAll = () => {
        if (onShowAll) {
            onShowAll();
        }
    };

    const renderSkeletons = () => {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 auto-rows-fr gap-3">
                {Array.from({ length: skeletonCount }).map((_, i) => (
                    <TopicCardSkeleton key={i} />
                ))}
            </div>
        );
    };

    const renderEmptyState = () => {
        if (!emptyState) return null;
        return (
            <Card className="p-6 text-center border-dashed border-stone-300 dark:border-stone-600">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-stone-100 dark:bg-stone-800/30 flex items-center justify-center">
                    <emptyState.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-base font-semibold mb-1.5">{emptyState.title}</h3>
                <p className="text-sm text-muted-foreground mb-3">
                    {emptyState.description}
                </p>
                {emptyState.action && (
                    <Button
                        onClick={emptyState.action.onClick}
                        className="gap-2 rounded-full bg-primary-strong hover:bg-primary-strong/90"
                    >
                        {emptyState.action.icon && <emptyState.action.icon className="w-4 h-4" />}
                        {emptyState.action.label}
                    </Button>
                )}
            </Card>
        );
    };

    const renderSessions = () => {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 auto-rows-fr gap-3">
                {sessions.map((session) => (
                    <TopicCard
                        key={session.id}
                        session={session}
                        variant={variant}
                        onView={() => onView(session)}
                        onPractice={() => onPractice(session)}
                        showViewButton={showViewButton}
                        badgeExtra={renderCardBadge?.(session)}
                    />
                ))}
            </div>
        );
    };

    const renderContent = () => {
        if (showSkeletons) {
            return renderSkeletons();
        }
        if (sessions.length === 0 && emptyState) {
            return renderEmptyState();
        }
        return renderSessions();
    };

    return (
        <section data-tour={dataTour}>
            <div className="flex items-center justify-between mb-2 md:mb-3">
                <h2 className="text-sm md:text-lg font-bold flex items-center gap-1.5 md:gap-2">
                    <div className="p-1 md:p-1.5 rounded-lg bg-stone-100 dark:bg-stone-800/30">
                        <Icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
                    </div>
                    {title}
                </h2>
                <div className="flex items-center gap-2">
                    {isFetching && hasData && (
                        <div className="h-2 w-2 rounded-full bg-primary/60 animate-pulse" />
                    )}
                    {(showAllPath || onShowAll) && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleShowAll}
                            className="gap-1 text-muted-foreground hover:text-primary"
                        >
                            {t("common.actions.viewAll", "View All")}
                            <ArrowRight className="w-4 h-4" />
                        </Button>
                    )}
                </div>
            </div>

            {renderContent()}
        </section>
    );
});

export default TopicListSection;
